import React from 'react';
import { X, Link, FileJson, Trash2 } from 'lucide-react';

interface DataModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'export' | 'import';
  onExportJson?: () => void;
  onExportLink?: () => void;
  onImportJson?: (file: File) => void;
  onImportLink?: (link: string) => void;
  onClearData?: () => void;
}

export function DataModal({
  isOpen,
  onClose,
  type,
  onExportJson,
  onExportLink,
  onImportJson,
  onImportLink,
  onClearData
}: DataModalProps) {
  const [linkInput, setLinkInput] = React.useState('');
  const [shareableLink, setShareableLink] = React.useState('');
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && onImportJson) {
      onImportJson(file);
      onClose();
    }
  };

  const handleImportLink = () => {
    if (onImportLink && linkInput) {
      onImportLink(linkInput);
      setLinkInput('');
      onClose();
    }
  };

  const handleExportLink = () => {
    if (onExportLink) {
      const link = onExportLink();
      setShareableLink(link);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(shareableLink);
    alert('Link copied to clipboard!');
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-semibold">
            {type === 'export' ? 'Export Data' : 'Import Data'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {type === 'export' ? (
            <div className="space-y-4">
              <button
                onClick={onExportJson}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 
                         bg-blue-600 text-white rounded-lg hover:bg-blue-700 
                         transition-colors"
              >
                <FileJson size={20} />
                Export as JSON
              </button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">Or</span>
                </div>
              </div>

              <button
                onClick={handleExportLink}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 
                         border border-blue-600 text-blue-600 rounded-lg 
                         hover:bg-blue-50 transition-colors"
              >
                <Link size={20} />
                Generate Shareable Link
              </button>

              {shareableLink && (
                <div className="mt-4 space-y-2">
                  <input
                    type="text"
                    value={shareableLink}
                    readOnly
                    className="w-full px-3 py-2 border rounded-lg bg-gray-50"
                  />
                  <button
                    onClick={copyToClipboard}
                    className="w-full px-4 py-2 bg-gray-100 text-gray-700 
                             rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Copy Link
                  </button>
                </div>
              )}
              
              <div className="relative pt-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">Danger Zone</span>
                </div>
              </div>

              <button
                onClick={onClearData}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 
                         border border-red-600 text-red-600 rounded-lg 
                         hover:bg-red-50 transition-colors"
              >
                <Trash2 size={20} />
                Clear All Data
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <label className="w-full flex items-center justify-center gap-2 px-4 py-3 
                              bg-blue-600 text-white rounded-lg hover:bg-blue-700 
                              transition-colors cursor-pointer">
                <FileJson size={20} />
                Import from JSON
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </label>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">Or</span>
                </div>
              </div>

              <div className="space-y-2">
                <input
                  type="text"
                  value={linkInput}
                  onChange={(e) => setLinkInput(e.target.value)}
                  placeholder="Paste shared link here"
                  className="w-full px-3 py-2 border rounded-lg"
                />
                <button
                  onClick={handleImportLink}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 
                           border border-blue-600 text-blue-600 rounded-lg 
                           hover:bg-blue-50 transition-colors"
                  disabled={!linkInput}
                >
                  <Link size={20} />
                  Import from Link
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}